import {
  AudioFile,
  Star,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import DocumentIcon from "assets/icons/dashboard/Document";
import PDFIcon from "assets/icons/dashboard/PDF";
import { transformPatientToDetailData } from "components/features/patient/mappers/patientMapper";
import FinalizingTreatmentPlanModal from "components/features/TreatmentPlan/FinalizingTreatmentPlanModal";
import LeaveSiteModal from "components/features/TreatmentPlan/LeaveSiteModal";
import UnsavedFeedbackModal from "components/features/TreatmentPlan/UnsavedFeedbackModal";
import DashboardLayout from "components/layouts/DashboardLayout";
import { EPath } from "constants/path";
import { format } from "date-fns";
import { usePatientDetailForProvider } from "hooks/usePatientDetailForProvider";
import {
  useGeneratePdf,
  useSendFeedback,
  useTreatmentPlanDetail,
} from "hooks/useTreatmentPlan";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";
import { useCelleraTheme } from "theme/useCelleraTheme";

// Enums for patient details
enum PatientInfoType {
  EMAIL = "Email",
  PHONE = "Phone",
  BIRTHDAY = "Birthday",
}

const GeneratedProtocolResultPage: React.FC = () => {
  const navigate = useNavigate();
  const { patientId, treatmentId } = useParams<{
    patientId: string;
    treatmentId: string;
  }>();
  const [feedback, setFeedback] = useState("");
  const [originalFeedback, setOriginalFeedback] = useState(""); // Track original feedback from server
  const [isPollingForPdf, setIsPollingForPdf] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isLeaveSiteModalOpen, setIsLeaveSiteModalOpen] = useState(false);
  const [isUnsavedFeedbackModalOpen, setIsUnsavedFeedbackModalOpen] =
    useState(false);
  const [isPendingSendFeedback, setIsPendingSendFeedback] = useState(false);
  const pendingNavigationRef = useRef<string | null>(null);
  const { colors } = useCelleraTheme();
  const { sendFeedback, isSending } = useSendFeedback();
  const { generatePdf, isGenerating: isGeneratingPdf } = useGeneratePdf();
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const initialPollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sendFeedbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup polling intervals and timeouts on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (initialPollIntervalRef.current) {
        clearInterval(initialPollIntervalRef.current);
      }
      if (sendFeedbackTimeoutRef.current) {
        clearTimeout(sendFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const handleViewDetails = () => {
    if (patientId) {
      navigate(EPath.PATIENT_DASHBOARD.replace(":patientId", patientId));
    }
  };

  const handleSendFeedback = () => {
    if (!treatmentId || !feedback.trim()) return;

    // Clear any existing timeout
    if (sendFeedbackTimeoutRef.current) {
      clearTimeout(sendFeedbackTimeoutRef.current);
    }

    // Start pending state with 2.5 second delay
    setIsPendingSendFeedback(true);

    // Set timeout to actually send feedback after 2.5 seconds
    sendFeedbackTimeoutRef.current = setTimeout(async () => {
      setIsPendingSendFeedback(false);
      try {
        await sendFeedback(treatmentId, feedback);
        setOriginalFeedback(feedback); // Update original after successful send
        await refetchTreatmentPlan();
      } catch (error) {
        console.error("Failed to send feedback:", error);
      }
      sendFeedbackTimeoutRef.current = null;
    }, 2500);
  };

  const handleStopSendFeedback = () => {
    // Clear the timeout immediately to prevent feedback from being sent
    if (sendFeedbackTimeoutRef.current) {
      clearTimeout(sendFeedbackTimeoutRef.current);
      sendFeedbackTimeoutRef.current = null;
    }

    // Open modal to confirm cancellation
    setIsUnsavedFeedbackModalOpen(true);
  };

  const handleCancelSendFeedback = () => {
    // Clear pending state and close modal
    setIsPendingSendFeedback(false);
    setIsUnsavedFeedbackModalOpen(false);
  };

  const handleResumeSendFeedback = () => {
    if (!treatmentId || !feedback.trim()) {
      setIsUnsavedFeedbackModalOpen(false);
      return;
    }

    setIsUnsavedFeedbackModalOpen(false);
    setIsPendingSendFeedback(true);

    // Set timeout to actually send feedback after 2.5 seconds
    sendFeedbackTimeoutRef.current = setTimeout(async () => {
      setIsPendingSendFeedback(false);
      try {
        await sendFeedback(treatmentId, feedback);
        setOriginalFeedback(feedback); // Update original after successful send
        await refetchTreatmentPlan();
      } catch (error) {
        console.error("Failed to send feedback:", error);
      }
      sendFeedbackTimeoutRef.current = null;
    }, 2500);
  };

  // Handle leaving page with unsaved feedback (discard changes and navigate)
  const handleLeaveWithUnsavedFeedback = useCallback(() => {
    setFeedback(originalFeedback); // Revert to original
    setIsUnsavedFeedbackModalOpen(false);

    const path = pendingNavigationRef.current;
    pendingNavigationRef.current = null;

    if (path) {
      navigate(path);
    } else {
      navigate(-1);
    }
  }, [originalFeedback, navigate]);

  // Handle staying on page (close modal and keep unsaved changes)
  const handleStayWithUnsavedFeedback = useCallback(() => {
    setIsUnsavedFeedbackModalOpen(false);
    pendingNavigationRef.current = null;
  }, []);

  const handleFinalizeClick = () => {
    setIsFinalizeModalOpen(true);
  };

  const handleFinalizeConfirm = async () => {
    if (!treatmentId || isFinalizing) {
      return;
    }

    setIsFinalizing(true);

    // Clear any existing polling intervals before starting new one
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    try {
      await generatePdf(treatmentId);

      setIsFinalizeModalOpen(false);

      // Start polling for pdfReportUrl immediately after generating PDF
      setIsPollingForPdf(true);

      // Initial refetch after a short delay
      setTimeout(async () => {
        await refetchTreatmentPlan();
      }, 2000);

      let attempts = 0;
      const maxAttempts = 60; // 2 minutes total (60 attempts * 2 seconds)
      const pollIntervalMs = 2000;

      pollIntervalRef.current = setInterval(async () => {
        attempts++;

        try {
          await refetchTreatmentPlan();

          // Check if pdfReportUrl is now available after refetch
          // This will be handled in the useEffect hook below
        } catch (error) {
          console.error("Error polling for PDF:", error);
        }

        // Stop polling if max attempts reached
        if (attempts >= maxAttempts) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsPollingForPdf(false);
          console.warn("PDF generation polling timeout reached");
        }
      }, pollIntervalMs);
    } catch (error) {
      console.error("Failed to finalize treatment plan:", error);
      // Don't close modal on error so user can retry
      setIsPollingForPdf(false);
    } finally {
      setIsFinalizing(false);
    }
  };

  const { refetch: refetchTreatmentPlan, ...treatmentPlan } =
    useTreatmentPlanDetail({
      treatmentId: treatmentId || "",
      enabled: !!treatmentId,
    });

  const { patient } = usePatientDetailForProvider(patientId || "");

  const patientDetailData = patient
    ? transformPatientToDetailData(patient)
    : null;

  // Update feedback input when treatmentPlan feedback is loaded or updated
  useEffect(() => {
    if (treatmentPlan?.feedback) {
      const feedbackText =
        typeof treatmentPlan.feedback === "string"
          ? treatmentPlan.feedback
          : (treatmentPlan.feedback as { feedback?: string })?.feedback || "";
      if (feedbackText) {
        setFeedback(feedbackText);
        setOriginalFeedback(feedbackText); // Update original feedback as well
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan?.feedback]);

  // Check if there are unsaved feedback changes
  const hasUnsavedFeedback = useMemo(() => {
    return (
      feedback.trim() !== originalFeedback.trim() && feedback.trim() !== ""
    );
  }, [feedback, originalFeedback]);

  // Helper function to get file size and format
  const getFileInfo = (url: string | undefined, type: string) => {
    if (!url) return { size: "N/A", format: type };
    return { size: "N/A", format: type }; // Size would need to come from backend
  };

  const getFileName = (type: string, createdAt: Date) => {
    const fileName =
      "Personalized Wellness Protocol " +
      (patientDetailData?.name ?? "") +
      " - " +
      format(new Date(createdAt), "yyyyMMdd_HHmmss") +
      `.${type}`;
    return fileName;
  };

  // Helper to check if URL is valid
  const isValidUrl = (url: string | undefined | null): boolean => {
    return !!url && url !== "null" && url.trim() !== "";
  };

  const pdfReportInfo = getFileInfo(treatmentPlan?.pdfReportUrl, "PDF");
  const protocolInfo = getFileInfo(treatmentPlan?.txtUrl, "DOCX");
  const podcastInfo = getFileInfo(treatmentPlan?.audioUrl, "MP3");

  const hasPdfReportUrl = isValidUrl(treatmentPlan?.pdfReportUrl);
  // Check both txtUrl (signed URL) and txtPath (file path) since backend might return either
  const hasTxtUrl = isValidUrl(treatmentPlan?.txtPath);
  // Check both audioUrl (signed URL) and audioPath (file path) since backend might return either
  const hasAudioUrl = isValidUrl(treatmentPlan?.audioPath);

  // Check if all required files are available (excluding generatedPdf as it's not needed at this stage)
  const hasAllFiles = hasTxtUrl && hasAudioUrl;

  // Auto-polling when component mounts and no files are available
  useEffect(() => {
    if (!treatmentId) {
      return;
    }

    // Check if all required files are already available before starting polling
    // Only check txt and audio files (PDF is generated only when finalizing)
    // Check both Url and Path since backend might return either
    const checkAllFilesAvailable = () => {
      const txt = isValidUrl(treatmentPlan?.txtPath);
      const audio = isValidUrl(treatmentPlan?.audioPath);
      return txt && audio;
    };

    if (checkAllFilesAvailable() || isLoadingFiles) {
      return;
    }

    // Start polling for files
    setIsLoadingFiles(true);
    let attempts = 0;
    const maxAttempts = 150; // Try 150 times (5 minutes total at 2 seconds each)
    const pollIntervalMs = 2000; // Check every 2 seconds

    const startPolling = () => {
      initialPollIntervalRef.current = setInterval(async () => {
        attempts++;

        try {
          await refetchTreatmentPlan();
          // The useEffect hook will stop polling when hasAllFiles becomes true
        } catch (error) {
          console.error("Error polling for files:", error);
        }

        // Stop polling if max attempts reached
        if (attempts >= maxAttempts) {
          if (initialPollIntervalRef.current) {
            clearInterval(initialPollIntervalRef.current);
            initialPollIntervalRef.current = null;
          }
          setIsLoadingFiles(false);
        }
      }, pollIntervalMs);
    };

    // Start polling after a short delay
    const timeoutId = setTimeout(startPolling, 1000);

    return () => {
      clearTimeout(timeoutId);
      if (initialPollIntervalRef.current) {
        clearInterval(initialPollIntervalRef.current);
        initialPollIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentId]);

  // Stop initial polling when all required files become available
  useEffect(() => {
    if (hasAllFiles && isLoadingFiles) {
      if (initialPollIntervalRef.current) {
        clearInterval(initialPollIntervalRef.current);
        initialPollIntervalRef.current = null;
      }
      setIsLoadingFiles(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAllFiles, isLoadingFiles]);
  // When pdfReportUrl is available, refetch all 3 files (txtUrl, audioUrl, pdfReportUrl)
  useEffect(() => {
    const hasPdf = isValidUrl(treatmentPlan?.pdfReportUrl);
    if (hasPdf && isPollingForPdf) {
      // Clear polling interval
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPollingForPdf(false);

      // Refetch treatment plan to get updated URLs for all 3 files
      setTimeout(async () => {
        try {
          await refetchTreatmentPlan();
          console.log(
            "Refetched treatment plan after PDF generation completed"
          );
        } catch (error) {
          console.error(
            "Error refetching treatment plan after PDF generation:",
            error
          );
        }
      }, 1000);
    }
  }, [treatmentPlan?.pdfReportUrl, isPollingForPdf, refetchTreatmentPlan]);

  // Handle navigation from menu/sidebar
  const handleNavigation = useCallback(
    (path: string) => {
      pendingNavigationRef.current = path;
      // Check if there are unsaved feedback changes
      if (hasUnsavedFeedback) {
        setIsUnsavedFeedbackModalOpen(true);
      } else {
        setIsLeaveSiteModalOpen(true);
      }
    },
    [hasUnsavedFeedback]
  );

  return (
    <DashboardLayout
      title="Generated Protocol Result"
      onNavigate={handleNavigation}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          p: 3,
          gap: 3,
        }}
      >
        {/* Breadcrumbs and Action Bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Dashboard / Treatment Plan / Personalized Wellness Protocol /
            Generate Protocol
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Back Button */}
            <IconButton
              onClick={() => {
                pendingNavigationRef.current = null; // Will navigate back
                // Check if there are unsaved feedback changes
                if (hasUnsavedFeedback) {
                  setIsUnsavedFeedbackModalOpen(true);
                } else {
                  setIsLeaveSiteModalOpen(true);
                }
              }}
              size="small"
              sx={{
                borderRadius: 1,
              }}
            >
              <ArrowBackIcon />
            </IconButton>

            {/* Refresh Button */}
            <IconButton
              onClick={async () => {
                await refetchTreatmentPlan();
              }}
              size="small"
              sx={{
                borderRadius: 1,
              }}
            >
              <RefreshIcon />
            </IconButton>

            {/* Finalize Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleFinalizeClick}
              disabled={isFinalizing || isGeneratingPdf || isPollingForPdf}
              startIcon={
                isFinalizing || isGeneratingPdf || isPollingForPdf ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : null
              }
              sx={{
                backgroundColor: colors.primary.main,
                color: colors.primary.contrast,
                borderRadius: "24px",
                px: 3,
                py: 1.5,
                "&:hover": {
                  backgroundColor: colors.primary.dark,
                },
                "&:disabled": {
                  backgroundColor: colors.primary.main,
                  opacity: 0.6,
                },
              }}
            >
              {isFinalizing || isGeneratingPdf
                ? "Finalizing..."
                : isPollingForPdf
                  ? "Waiting for PDF..."
                  : "Finalize"}
            </Button>
          </Box>
        </Box>

        {/* Combined Documents and Doctor's Feedback Section */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
          }}
        >
          {/* Page Title */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
            }}
          >
            <Typography variant="h4">Generated Result</Typography>
          </Box>
          {(isLoadingFiles || isPollingForPdf) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                p: 2,
              }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                {isLoadingFiles
                  ? "Loading files..."
                  : isPollingForPdf
                    ? "Generating PDF, please wait..."
                    : ""}
              </Typography>
            </Box>
          )}
          {/* Documents Section */}
          {!hasPdfReportUrl && !hasTxtUrl && !hasAudioUrl ? (
            <Box
              sx={{
                p: 3,
                textAlign: "center",
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No files available yet
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 2,
                p: 2,
              }}
            >
              {/* PDF Report - Original Treatment Plan PDF */}
              {hasPdfReportUrl && (
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    borderRadius: 2,
                  }}
                >
                  <PDFIcon />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1">
                      {getFileName("pdf", treatmentPlan?.createdAt ?? "")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {treatmentPlan?.createdAt
                        ? format(
                            new Date(treatmentPlan?.createdAt),
                            "MMM dd • HH:mm"
                          )
                        : "N/A"}{" "}
                      • {pdfReportInfo.size} • {pdfReportInfo.format} Format
                    </Typography>
                  </Box>
                  <IconButton
                    component="a"
                    href={treatmentPlan?.pdfReportUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={
                      isGeneratingPdf || isPollingForPdf || !hasPdfReportUrl
                    }
                    sx={{
                      color: colors.text.secondary,
                      "&:hover": {
                        color: colors.primary.main,
                      },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Paper>
              )}

              {/* Protocol (Text) */}
              {hasTxtUrl && (
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    borderRadius: 2,
                  }}
                >
                  <DocumentIcon />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1">
                      {getFileName("docx", treatmentPlan?.createdAt ?? "")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {treatmentPlan?.createdAt
                        ? format(
                            new Date(treatmentPlan?.createdAt),
                            "MMM dd • HH:mm"
                          )
                        : "N/A"}{" "}
                      • {protocolInfo.size} • DOCX Format
                    </Typography>
                  </Box>
                  <IconButton
                    component="a"
                    href={treatmentPlan?.txtUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={isGeneratingPdf || isPollingForPdf || !hasTxtUrl}
                    sx={{
                      color: colors.text.secondary,
                      "&:hover": {
                        color: colors.primary.main,
                      },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Paper>
              )}

              {/* Podcast (Audio) */}
              {hasAudioUrl && hasPdfReportUrl && (
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    borderRadius: 2,
                  }}
                >
                  <AudioFile sx={{ fontSize: 40, color: "primary.main" }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1">
                      {getFileName("mp3", treatmentPlan?.createdAt ?? "")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {treatmentPlan?.createdAt
                        ? format(
                            new Date(treatmentPlan?.createdAt),
                            "MMM dd • HH:mm"
                          )
                        : "N/A"}{" "}
                      • {podcastInfo.size} • {podcastInfo.format} Format
                    </Typography>
                  </Box>
                  <IconButton
                    component="a"
                    href={treatmentPlan?.audioUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={
                      isGeneratingPdf || isPollingForPdf || !hasAudioUrl
                    }
                    sx={{
                      color: colors.text.secondary,
                      "&:hover": {
                        color: colors.primary.main,
                      },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Paper>
              )}
            </Box>
          )}

          {/* Doctor's Feedback */}
          <Box
            sx={{
              p: 3,
              position: "relative",
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Doctor&apos;s Feedback
            </Typography>

            <TextField
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              placeholder="Enter your feedback here..."
              value={feedback}
              onChange={(e) => {
                if (e.target.value.length <= 2000) {
                  setFeedback(e.target.value);
                }
              }}
              inputProps={{ maxLength: 2000 }}
              disabled={
                isGeneratingPdf || isPollingForPdf || isPendingSendFeedback
              }
              sx={{ mb: 2 }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {feedback.length}/2000
              </Typography>
              {isPendingSendFeedback ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  }
                  endIcon={<StopIcon />}
                  onClick={handleStopSendFeedback}
                  sx={{
                    backgroundColor: colors.primary.main,
                    color: colors.primary.contrast,
                    "&:hover": {
                      backgroundColor: colors.primary.dark,
                    },
                  }}
                >
                  Stop
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    isSending ? (
                      <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                      <Star />
                    )
                  }
                  onClick={handleSendFeedback}
                  disabled={isSending}
                  sx={{
                    backgroundColor: colors.primary.main,
                    color: colors.primary.contrast,
                    "&:hover": {
                      backgroundColor: colors.primary.dark,
                    },
                    "&:disabled": {
                      backgroundColor: colors.primary.main,
                      opacity: 0.6,
                    },
                  }}
                >
                  {isSending ? "Sending..." : "Send Feedback"}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Patient Information */}
        {patientDetailData && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: "primary.main",
              }}
            >
              {patientDetailData.name?.[0]}
            </Avatar>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h6">{patientDetailData.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {PatientInfoType.EMAIL}: {patientDetailData.email || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {PatientInfoType.BIRTHDAY}:{" "}
                  {patientDetailData.personalInfo.dateOfBirth || "N/A"}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  gap: 0.5,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {PatientInfoType.PHONE}:{" "}
                  {patientDetailData.personalInfo?.phone || "N/A"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip
                    label={
                      patientDetailData.status === "active_sharing"
                        ? "ACTIVE SHARING"
                        : patientDetailData.status || "inactive"
                    }
                    color={
                      patientDetailData.status === "active_sharing"
                        ? "success"
                        : "default"
                    }
                    size="small"
                  />
                </Box>
              </Box>
              <Button
                variant="contained"
                onClick={handleViewDetails}
                sx={{
                  backgroundColor: "#1A237E",
                  color: "white",
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  "&:hover": {
                    backgroundColor: "#0D47A1",
                  },
                }}
              >
                View Details
              </Button>
            </Box>
          </Paper>
        )}

        {/* Patient Details */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 3,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 3,
            }}
          >
            {/* Personal Information */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Personal Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Date of Birth:
                </Typography>
                <Typography variant="body2">
                  {patientDetailData?.personalInfo.dateOfBirth || "N/A"}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Phone:
                </Typography>
                <Typography variant="body2">
                  {patientDetailData?.personalInfo.phone || "N/A"}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Address:
                </Typography>
                <Typography variant="body2">
                  {patientDetailData?.personalInfo.address || "N/A"}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Emergency Contact:
                </Typography>
                <Typography variant="body2">
                  {patientDetailData?.personalInfo.emergencyContact || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 3,
            }}
          >
            {/* Medical Information */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Medical Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Blood Type:
                </Typography>
                <Typography variant="body2">
                  {patientDetailData?.medicalInfo.bloodType || "N/A"}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Allergies:
                </Typography>
                <Typography variant="body2">
                  {patientDetailData?.medicalInfo.allergies || "N/A"}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Conditions:
                </Typography>
                <Typography variant="body2">
                  {patientDetailData?.medicalInfo.conditions || "N/A"}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Insurance Status:
                </Typography>
                <Typography variant="body2">
                  {patientDetailData?.medicalInfo.insuranceStatus || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 3,
            }}
          >
            {/* Billing Information*/}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Billing Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Billing Status:
                </Typography>
                <Typography variant="body2">
                  {patientDetailData?.billingInfo.billingStatus || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Payment:
                </Typography>
                <Typography variant="body2">
                  {patientDetailData?.billingInfo.payment || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Payment Type:
                </Typography>
                <Typography variant="body2">
                  {patientDetailData?.billingInfo.paymentType || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Payment Profile:
                </Typography>
                <Typography variant="body2">
                  {patientDetailData?.billingInfo.paymentProfile || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Finalizing Treatment Plan Modal */}
      <FinalizingTreatmentPlanModal
        open={isFinalizeModalOpen}
        onClose={() => setIsFinalizeModalOpen(false)}
        onConfirm={handleFinalizeConfirm}
        isConfirming={isFinalizing}
      />

      {/* Leave Site Modal */}
      <LeaveSiteModal
        open={isLeaveSiteModalOpen}
        onClose={() => {
          setIsLeaveSiteModalOpen(false);
          pendingNavigationRef.current = null;
        }}
        onLeave={() => {
          setIsLeaveSiteModalOpen(false);
          if (pendingNavigationRef.current) {
            navigate(pendingNavigationRef.current);
          } else {
            navigate(-1);
          }
          pendingNavigationRef.current = null;
        }}
      />

      {/* Unsaved Feedback Modal */}
      <UnsavedFeedbackModal
        open={isUnsavedFeedbackModalOpen}
        onClose={
          isPendingSendFeedback
            ? handleResumeSendFeedback
            : handleStayWithUnsavedFeedback
        }
        onLeave={
          isPendingSendFeedback
            ? handleCancelSendFeedback
            : handleLeaveWithUnsavedFeedback
        }
      />
    </DashboardLayout>
  );
};

export default GeneratedProtocolResultPage;
